import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, History, Smartphone } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Locked } from "@/components/Locked";
import { CreateAlertForm } from "@/components/alerts/CreateAlertForm";
import { AlertRow } from "@/components/alerts/AlertRow";
import { PushSettings } from "@/components/alerts/PushSettings";

const TABS = ["active", "history", "notifications"];

export default function Alerts() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "active";
  const highlight = params.get("alert");
  const [alerts, setAlerts] = useState([]);
  const [notifs, setNotifs] = useState({});

  const load = useCallback(() => {
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(Object.fromEntries((r.data.notifications || []).filter((n) => n.alert_id).map((n) => [n.alert_id, n])))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("apx:notifications-changed", load); return () => window.removeEventListener("apx:notifications-changed", load); }, [load]);

  const remove = async (id) => { await api.delete(`/alerts/${id}`); load(); };
  const active = alerts.filter((a) => a.status === "active");
  const done = alerts.filter((a) => a.status !== "active");
  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Alerts & Notifications</h1>
        <p className="text-sm text-slate-400 mt-1">Price targets, ranges, support/resistance breaks, brackets and EV-IDEX signal alerts — checked live and every 15 minutes. Delivered in-app, and as push notifications to devices you connect under <em>Phone notifications</em> (Firebase or browser push). SMS is not available.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800 w-full md:w-auto grid grid-cols-3 md:inline-flex" data-testid="alerts-tabs">
          <TabsTrigger value="active" data-testid="alerts-tab-active" className="gap-1.5"><Bell className="w-3.5 h-3.5" /> Active <span className="text-[10px] text-slate-500">({active.length})</span></TabsTrigger>
          <TabsTrigger value="history" data-testid="alerts-tab-history" className="gap-1.5"><History className="w-3.5 h-3.5" /> Triggered & expired <span className="text-[10px] text-slate-500">({done.length})</span></TabsTrigger>
          <TabsTrigger value="notifications" data-testid="alerts-tab-notifications" className="gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Phone notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-6 mt-4">
          <Locked feature="alerts" title="Custom alerts are a Pro feature" compact
            desc="Create price, range, support/resistance, volume, volatility and AI-signal alerts, arm stop/target brackets, and get in-app + phone notifications checked live and every 15 minutes. Alerts you already created stay listed below.">
            <CreateAlertForm onCreated={load} />
          </Locked>
          <div className="apx-card">
            <div className="p-4 border-b border-slate-800 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /><h3 className="font-display text-base font-semibold">Active alerts</h3></div>
            {active.length === 0 ? <div className="p-8 text-center text-sm text-slate-500" data-testid="no-alerts">No active alerts.</div>
              : <div className="divide-y divide-slate-900" data-testid="alerts-list">{active.map((a) => <AlertRow key={a.id} a={a} onDelete={remove} highlight={a.id === highlight} />)}</div>}
          </div>
          <div className="text-xs text-slate-500 apx-card p-4" data-testid="alerts-footnote">
            <strong className="text-amber-300">How checks work:</strong> price, range and support/resistance alerts fire instantly from the live exchange feed while you have the coin page open, and every 15 minutes in the background from CoinGecko market data. Volume, volatility and AI-signal-change alerts are checked every 15 minutes. Each alert fires once, then moves to <em>Triggered & expired</em>. <strong className="text-slate-300">No alert ever places a trade.</strong>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="apx-card">
            <div className="p-4 border-b border-slate-800 flex items-center gap-2"><History className="w-4 h-4 text-emerald-400" /><h3 className="font-display text-base font-semibold">Triggered & expired</h3></div>
            {done.length === 0 ? <div className="p-8 text-center text-sm text-slate-500" data-testid="no-history">Nothing has triggered or expired yet.</div>
              : <div className="divide-y divide-slate-900" data-testid="alerts-history-list">{done.map((a) => <AlertRow key={a.id} a={a} onDelete={remove} highlight={a.id === highlight} notification={notifs[a.id]} />)}</div>}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4"><PushSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
